import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

import Input from './Input';
import { TRANSLATION_FILES } from '../../constants/translation';
import {
  PASSWORD_MATCH_LABEL,
  PASSWORD_MIN_LENGTH,
  passwordRuleStatus,
} from '../../utils/validations/password';

// Password field with live policy feedback: a checklist under the input that
// turns each rule green (check) or red (cross) as the user types, and an input
// border that is red while any rule fails and green with a check icon once all
// pass. Pass `matchValue` for a confirm-password field; it then has a single
// rule, matching that value. Once the user has typed, the checklist is the only
// feedback: validator messages are hidden so the policy is not repeated in text.
const PasswordInput = forwardRef(({
  matchValue, value, onChange, icon, ...props
}, ref) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const current = value === undefined || value === null ? typed : String(value);
  const pristine = current.length === 0;
  const isConfirm = matchValue !== undefined;
  const rules = isConfirm
    ? [{ key: 'match', label: PASSWORD_MATCH_LABEL, satisfied: !pristine && current === matchValue }]
    : passwordRuleStatus(current);
  const valid = !pristine && rules.every(rule => rule.satisfied);
  const invalid = !pristine && !valid;

  const handleChange = (e, data) => {
    setTyped(data && data.value !== undefined ? data.value : e.target.value);
    if (onChange) onChange(e, data);
  };

  const rowState = (rule) => {
    if (pristine) return 'pristine';
    return rule.satisfied ? 'satisfied' : 'unsatisfied';
  };
  const rowIcon = (rule) => {
    if (pristine) return 'circle outline';
    return rule.satisfied ? 'check circle' : 'times circle';
  };

  return (
    <div className={`password-input${valid ? ' valid' : ''}${invalid ? ' invalid' : ''}`}>
      <Input
        {...props}
        ref={ref}
        type="password"
        value={value}
        onChange={handleChange}
        icon={valid ? <Icon name="check circle" color="green" /> : icon}
        hideMessage={!pristine}
      />
      <ul className="password-checklist" aria-live="polite">
        {rules.map(rule => (
          <li key={rule.key} className={rowState(rule)}>
            <Icon name={rowIcon(rule)} />
            {t(rule.label, { ns: TRANSLATION_FILES.translation, count: PASSWORD_MIN_LENGTH })}
          </li>
        ))}
      </ul>
    </div>
  );
});

PasswordInput.propTypes = {
  matchValue: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  icon: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.node,
  ]),
};

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;



// WEBPACK FOOTER //
// ./src/components/AppForm/PasswordInput.jsx